"use client";

import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { useState } from "react";
import type {
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
} from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";
import { ConfirmationModal } from "@/components";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { LabeledTextarea } from "@/components/shared/formElements/LabeledTextarea";
import { IconButton, Text, TouchAwareTooltip } from "@/components/ui";
import { useInteractiveTokens, useThemeTokens } from "@/lib/theme/tokens";
import { PHONE_MAX_LENGTH } from "@/lib/utils/phone/phone";
import type { SignUpFormSchema } from "@/schemas";

type FamilyContactFieldsProps = {
  index: number;
  fieldsLength: number;
  remove: UseFieldArrayRemove;
  errors: FieldErrors<SignUpFormSchema>;
  register: UseFormRegister<SignUpFormSchema>;
  field: FieldArrayWithId<SignUpFormSchema, "familyContacts", "id">;
};

export const FamilyContactFields = ({
  index,
  field,
  remove,
  errors,
  register,
  fieldsLength,
}: FamilyContactFieldsProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  return (
    <Box key={field.id} mb="24px">
      <Flex mb="16px" height="40px" align="center" justify="space-between">
        <Text variant="primary" fontSize="lg" fontWeight="600">
          Family contact #{index + 1}
        </Text>

        {fieldsLength > 1 && (
          <TouchAwareTooltip
            label="Remove family contact"
            placement="top"
            openDelay={300}
          >
            <IconButton
              aria-label="Remove family contact"
              icon={<FiTrash2 size={18} />}
              size="sm"
              variant="ghost"
              color={theme.text.tertiary}
              onClick={() => setIsRemoveModalOpen(true)}
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    color: "red.400",
                    bg: interactive.neutral.bg,
                  },
                },
              }}
            />
          </TouchAwareTooltip>
        )}
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
        <LabeledInput
          name={`familyContacts.${index}.firstName`}
          label="First Name"
          errors={errors}
          register={register}
          placeholder="Enter first name"
          required
        />
        <LabeledInput
          name={`familyContacts.${index}.lastName`}
          label="Last Name"
          errors={errors}
          register={register}
          placeholder="Enter last name"
          required
        />
        <LabeledInput
          name={`familyContacts.${index}.email`}
          label="Email"
          errors={errors}
          register={register}
          type="email"
          placeholder="mail@example.com"
          maxLength={50}
          required
        />
        <LabeledInput
          name={`familyContacts.${index}.cellPhone`}
          label="Primary Phone"
          errors={errors}
          register={register}
          placeholder="Phone number"
          maxLength={PHONE_MAX_LENGTH}
          required
        />
      </SimpleGrid>

      <Box mt="20px">
        <LabeledTextarea
          name={`familyContacts.${index}.addressLine1`}
          label="Address"
          errors={errors}
          register={register}
          placeholder="Enter address"
          rows={3}
          required
        />
      </Box>

      <ConfirmationModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={() => {
          remove(index);
          setIsRemoveModalOpen(false);
        }}
        title="Remove family contact"
        message="This family contact will be removed from the form."
        confirmText="Remove"
        confirmColorScheme="red"
      />
    </Box>
  );
};
